import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';

/** Shape of every Response<T> envelope returned by fps-api-v2. */
interface ApiEnvelope<T> {
  data: T;
  isSuccess: boolean;
  statusCode: number;
  message: string;
  errorType: string | null;
}
import { LoggingService } from '../../../core/services/logging.service';
import { BaseRepository } from './base.repository';
import envConfig from '../../../env-config';
import type {
  DiagnosticResult,
  SystemHealth,
  FixVisibilityResult,
  FileDiagnosticResult,
  IngestionTrailDiagnosticResult,
  DataParityDiagnosticResult,
  EmailCallbackDiagnosticResult,
  SftpStorageListResult,
  ObjectStorageFilesResult,
  SftpAllSnapshotsResult,
} from '../../../features/diagnostics/models/diagnostics.models';

/** Provides HTTP access to the diagnostics BFF endpoints. */
@Injectable({ providedIn: 'root' })
export class DiagnosticsRepository extends BaseRepository {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(LoggingService);
  private readonly apiUrl = `${envConfig.bffUrl ?? ''}/api/v1/diagnostics`;
  private readonly fileApiUrl = `${envConfig.bffUrl ?? ''}/api/v1/file`;

  /**
   * Retrieves full diagnostics for a single request, including file verification,
   * ingestion trail, data parity, and email callback status.
   * @param requestId The numeric request ID.
   * @returns Observable emitting the diagnostic result; errors are logged and rethrown.
   */
  getRequestDiagnostic(requestId: number): Observable<DiagnosticResult> {
    this.logger.debug('DiagnosticsRepository: Fetching request diagnostic', {
      requestId,
    });
    return this.http
      .get<ApiEnvelope<DiagnosticResult>>(
        `${this.apiUrl}/requests/${requestId}`,
        {
          context: this.authContext(),
        }
      )
      .pipe(
        map(envelope => envelope.data),
        tap(() =>
          this.logger.debug(
            'DiagnosticsRepository: Request diagnostic fetched',
            { requestId }
          )
        ),
        catchError(error => {
          this.logger.error(
            'DiagnosticsRepository: Failed to fetch request diagnostic',
            error
          );
          throw error;
        })
      );
  }

  getFilesDiagnostic(requestId: number): Observable<FileDiagnosticResult> {
    return this.http
      .get<ApiEnvelope<FileDiagnosticResult>>(
        `${this.apiUrl}/requests/${requestId}/files`,
        {
          context: this.authContext(),
        }
      )
      .pipe(
        map(envelope => envelope.data),
        tap(() =>
          this.logger.debug(
            'DiagnosticsRepository: Request diagnostic fetched',
            { requestId }
          )
        ),
        catchError(error => {
          this.logger.error(
            'DiagnosticsRepository: Failed to fetch request diagnostic',
            error
          );
          throw error;
        })
      );
  }

  getDataParityDiagnosticResult(
    requestId: number
  ): Observable<DataParityDiagnosticResult> {
    return this.http
      .get<ApiEnvelope<DataParityDiagnosticResult>>(
        `${this.apiUrl}/requests/${requestId}/parity`,
        {
          context: this.authContext(),
        }
      )
      .pipe(
        map(envelope => envelope.data),
        tap(() =>
          this.logger.debug(
            'DiagnosticsRepository: Request diagnostic fetched',
            { requestId }
          )
        ),
        catchError(error => {
          this.logger.error(
            'DiagnosticsRepository: Failed to fetch request diagnostic',
            error
          );
          throw error;
        })
      );
  }

  getEmailCallbackDiagnosticResult(
    requestId: number
  ): Observable<EmailCallbackDiagnosticResult> {
    return this.http
      .get<ApiEnvelope<EmailCallbackDiagnosticResult>>(
        `${this.apiUrl}/requests/${requestId}/email`,
        {
          context: this.authContext(),
        }
      )
      .pipe(
        map(envelope => envelope.data),
        tap(() =>
          this.logger.debug(
            'DiagnosticsRepository: Request diagnostic fetched',
            { requestId }
          )
        ),
        catchError(error => {
          this.logger.error(
            'DiagnosticsRepository: Failed to fetch request diagnostic',
            error
          );
          throw error;
        })
      );
  }

  getIngestionTrail(
    requestId: number
  ): Observable<IngestionTrailDiagnosticResult> {
    return this.http
      .get<ApiEnvelope<IngestionTrailDiagnosticResult>>(
        `${this.apiUrl}/requests/${requestId}/ingestion`,
        {
          context: this.authContext(),
        }
      )
      .pipe(
        map(envelope => envelope.data),
        tap(() =>
          this.logger.debug(
            'DiagnosticsRepository: Request diagnostic fetched',
            { requestId }
          )
        ),
        catchError(error => {
          this.logger.error(
            'DiagnosticsRepository: Failed to fetch request diagnostic',
            error
          );
          throw error;
        })
      );
  }

  /**
   * Retrieves system health data: stuck requests (paged), rejected files,
   * service heartbeats, and infrastructure checks.
   * @param stuckPage Page number for the stuck requests list (1-based, default 1).
   * @param stuckPageSize Items per page for stuck requests (default 10).
   * @returns Observable emitting system health data; errors are logged and rethrown.
   */
  getSystemHealth(stuckPage = 1, stuckPageSize = 10): Observable<SystemHealth> {
    this.logger.debug('DiagnosticsRepository: Fetching system health', {
      stuckPage,
      stuckPageSize,
    });
    return this.http
      .get<ApiEnvelope<SystemHealth>>(`${this.apiUrl}/system-health`, {
        context: this.authContext(),
        params: {
          stuckPage: stuckPage.toString(),
          stuckPageSize: stuckPageSize.toString(),
        },
      })
      .pipe(
        map(envelope => envelope.data),
        tap(() =>
          this.logger.debug('DiagnosticsRepository: System health fetched')
        ),
        catchError(error => {
          this.logger.error(
            'DiagnosticsRepository: Failed to fetch system health',
            error
          );
          throw error;
        })
      );
  }

  getRejectedFiles(): Observable<SystemHealth> {
    this.logger.debug('DiagnosticsRepository: Fetching rejected files');
    return this.http
      .get<ApiEnvelope<SystemHealth>>(`${this.apiUrl}/system-health/rejected`, {
        context: this.authContext(),
      })
      .pipe(
        map(envelope => envelope.data),
        tap(() =>
          this.logger.debug('DiagnosticsRepository: System health fetched')
        ),
        catchError(error => {
          this.logger.error(
            'DiagnosticsRepository: Failed to fetch system health',
            error
          );
          throw error;
        })
      );
  }

  getServiceHeartbeat(): Observable<SystemHealth> {
    this.logger.debug(
      'DiagnosticsRepository: Fetching service health heart beat'
    );
    return this.http
      .get<ApiEnvelope<SystemHealth>>(
        `${this.apiUrl}/system-health/heartbeat`,
        {
          context: this.authContext(),
        }
      )
      .pipe(
        map(envelope => envelope.data),
        tap(() =>
          this.logger.debug('DiagnosticsRepository: System health fetched')
        ),
        catchError(error => {
          this.logger.error(
            'DiagnosticsRepository: Failed to fetch system health',
            error
          );
          throw error;
        })
      );
  }

  getInfraCheck(names: string): Observable<SystemHealth> {
    this.logger.debug('DiagnosticsRepository: Fetching infra check');
    return this.http
      .get<ApiEnvelope<SystemHealth>>(`${this.apiUrl}/system-health/infra`, {
        context: this.authContext(),
        params: {
          names,
        },
      })
      .pipe(
        map(envelope => envelope.data),
        tap(() =>
          this.logger.debug('DiagnosticsRepository: infra check fetched')
        ),
        catchError(error => {
          this.logger.error(
            'DiagnosticsRepository: Failed to fetch infra check',
            error
          );
          throw error;
        })
      );
  }

  /**
   * Fixes the IsVisibleOnGrid flag.
   * When `requestId` is provided, fixes only that request and returns `{ fixedCount: 1 }`.
   * When omitted, bulk-fixes all currently stuck requests.
   * @param requestId Optional request ID. Omit to fix all stuck requests.
   * @returns Observable emitting the fix result with how many requests were updated.
   */
  fixVisibility(requestId?: number): Observable<FixVisibilityResult> {
    this.logger.debug('DiagnosticsRepository: Fixing visibility', {
      requestId: requestId ?? 'bulk',
    });

    return this.http
      .put<ApiEnvelope<FixVisibilityResult>>(
        `${this.apiUrl}/requests/visibility`,
        null,
        {
          context: this.authContext(),
          ...(requestId !== undefined && {
            params: { requestId: requestId.toString() },
          }),
        }
      )
      .pipe(
        map(envelope => envelope.data),
        tap(result =>
          this.logger.debug('DiagnosticsRepository: Visibility fixed', {
            fixedCount: result.fixedCount,
          })
        ),
        catchError(error => {
          this.logger.error(
            'DiagnosticsRepository: Failed to fix visibility',
            error
          );
          throw error;
        })
      );
  }

  /**
   * Releases the processing lock on a stuck request.
   * @param requestId The request ID whose lock should be released.
   * @returns Observable completing on success; errors are logged and rethrown.
   */
  releaseProcessingLock(requestId: number): Observable<void> {
    this.logger.debug('DiagnosticsRepository: Releasing processing lock', {
      requestId,
    });
    return this.http
      .delete<void>(`${this.apiUrl}/requests/${requestId}/lock`, {
        context: this.authContext(),
      })
      .pipe(
        catchError(error => {
          this.logger.error(
            'DiagnosticsRepository: Failed to release processing lock',
            error
          );
          throw error;
        })
      );
  }

  /**
   * Queues a retry of the NetPay callback for a request.
   * @param requestId The request ID to retry.
   * @returns Observable completing on success; errors are logged and rethrown.
   */
  retryNetPayCallback(requestId: number): Observable<void> {
    this.logger.debug('DiagnosticsRepository: Retrying NetPay callback', {
      requestId,
    });
    return this.http
      .post<void>(
        `${this.apiUrl}/requests/${requestId}/netpay-callbacks/retry`,
        null,
        { context: this.authContext() }
      )
      .pipe(
        catchError(error => {
          this.logger.error(
            'DiagnosticsRepository: Failed to retry NetPay callback',
            error
          );
          throw error;
        })
      );
  }

  /**
   * Marks a file as ready to process.
   * Delegates to the File API endpoint (`PUT /api/v1/file/{fileId}/ready-to-process`).
   * @param fileId The file ID to mark ready.
   * @returns Observable completing on success; errors are logged and rethrown.
   */
  markFileReady(fileId: string, createdBy: string): Observable<void> {
    this.logger.debug('DiagnosticsRepository: Marking file ready', { fileId });
    return this.http
      .put<void>(
        `${this.fileApiUrl}/${fileId}/ready-to-process`,
        { ID: fileId, createdBy },
        {
          context: this.authContext(),
        }
      )
      .pipe(
        catchError(error => {
          this.logger.error(
            'DiagnosticsRepository: Failed to mark file ready',
            error
          );
          throw error;
        })
      );
  }

  /**
   * Retrieves files from input SFTP servers only.
   * Use this endpoint when you want independent resilience (one source timeout doesn't affect others).
   * @returns Observable emitting SFTP storage list; errors are logged and rethrown.
   */
  getStorageSftpInputFiles(): Observable<SftpStorageListResult> {
    this.logger.debug('DiagnosticsRepository: Fetching SFTP input files');
    return this.http
      .get<ApiEnvelope<SftpStorageListResult>>(
        `${this.apiUrl}/storage-files/sftp/input`,
        {
          context: this.authContext(),
        }
      )
      .pipe(
        map(envelope => envelope.data),
        tap(() =>
          this.logger.debug('DiagnosticsRepository: SFTP input files fetched')
        ),
        catchError(error => {
          this.logger.error(
            'DiagnosticsRepository: Failed to fetch SFTP input files',
            error
          );
          throw error;
        })
      );
  }

  /**
   * Retrieves files from output SFTP servers only.
   * Use this endpoint when you want independent resilience (one source timeout doesn't affect others).
   * @returns Observable emitting SFTP storage list; errors are logged and rethrown.
   */
  getStorageSftpOutputFiles(): Observable<SftpStorageListResult> {
    this.logger.debug('DiagnosticsRepository: Fetching SFTP output files');
    return this.http
      .get<ApiEnvelope<SftpStorageListResult>>(
        `${this.apiUrl}/storage-files/sftp/output`,
        {
          context: this.authContext(),
        }
      )
      .pipe(
        map(envelope => envelope.data),
        tap(() =>
          this.logger.debug('DiagnosticsRepository: SFTP output files fetched')
        ),
        catchError(error => {
          this.logger.error(
            'DiagnosticsRepository: Failed to fetch SFTP output files',
            error
          );
          throw error;
        })
      );
  }

  /**
   * Retrieves cached SFTP file snapshots for all hosts from the database.
   * @returns Observable emitting input and output server snapshots.
   */
  getStorageSftpSnapshots(): Observable<SftpAllSnapshotsResult> {
    this.logger.debug('DiagnosticsRepository: Fetching SFTP snapshots');
    return this.http
      .get<ApiEnvelope<SftpAllSnapshotsResult>>(
        `${this.apiUrl}/storage-files/sftp/snapshots`,
        { context: this.authContext() }
      )
      .pipe(
        map(envelope => envelope.data),
        tap(() =>
          this.logger.debug('DiagnosticsRepository: SFTP snapshots fetched')
        ),
        catchError(error => {
          this.logger.error(
            'DiagnosticsRepository: Failed to fetch SFTP snapshots',
            error
          );
          throw error;
        })
      );
  }

  /**
   * Retrieves files from object storage folders only.
   * Use this endpoint when you want independent resilience (one source timeout doesn't affect others).
   * @returns Observable emitting object storage files; errors are logged and rethrown.
   */
  getStorageObjectFiles(): Observable<ObjectStorageFilesResult> {
    this.logger.debug('DiagnosticsRepository: Fetching object storage files');
    return this.http
      .get<ApiEnvelope<ObjectStorageFilesResult>>(
        `${this.apiUrl}/storage-files/object-storage`,
        {
          context: this.authContext(),
        }
      )
      .pipe(
        map(envelope => envelope.data),
        tap(() =>
          this.logger.debug(
            'DiagnosticsRepository: Object storage files fetched'
          )
        ),
        catchError(error => {
          this.logger.error(
            'DiagnosticsRepository: Failed to fetch object storage files',
            error
          );
          throw error;
        })
      );
  }
}
